import { defineStore } from 'pinia'
import { GroupSize } from '@/modules/InfoCollectionChat/utils'
import { createApi } from '@/utils/api'
import { deserializeJsonApi } from '../utils/jsonApiDeserializer'
import { AxiosError } from 'axios'
import { CustomItinerary, DateOnly } from '../types'

const api = createApi()

export enum AutoSaveFields {
  DATE_RANGE = 'dateRange',
  GROUP_SIZE = 'groupSize',
  MARKUP = 'markup',
}

interface Loading {
  dateRange: boolean
  groupSize: boolean
  markup: boolean
}

interface Errors {
  dateRange: string[] | null
  groupSize: string[] | null
  markup: string[] | null
}

export const usePublishSetupStore = defineStore('publish-setup', {
  state: (): {
    groupSize: string | null
    selectedDateRange: { startDate: Date; endDate: Date } | null
    isMarkupEnabled: boolean
    markup: number
    availability: ItineraryAvailabilities['available_date_range'] | null
    price: number | null
    loading: Loading
    errors: Errors
    isFeedbackOpen: boolean
  } => ({
    groupSize: null,
    selectedDateRange: null,
    isMarkupEnabled: false,
    markup: 0,
    availability: null,
    price: null,
    loading: {
      dateRange: false,
      groupSize: false,
      markup: false,
    },
    errors: {
      dateRange: null,
      groupSize: null,
      markup: null,
    },
    isFeedbackOpen: false,
  }),
  getters: {
    minimumGroupSize(state): number {
      if (state.groupSize === GroupSize.TWENTY_ONE_PLUS || state.groupSize === '21+') {
        return 21
      }
      if (state.groupSize?.includes('to')) {
        return Number(state.groupSize.split('to')[0] ?? 0)
      }
      return Number(state.groupSize?.split('-')[0] ?? 0)
    },
    potentialEarnings(): number {
      return this.minimumGroupSize * this.markup
    },
    earningBreakdown(): string {
      return `${this.markup} x ${this.minimumGroupSize} minimum travelers`
    },
    hasErrors(state): boolean {
      return (
        (state.errors.dateRange?.length ?? 0) > 0 ||
        (state.errors.groupSize?.length ?? 0) > 0 ||
        (state.errors.markup?.length ?? 0) > 0
      )
    },
    isLoading(state): boolean {
      return state.loading.dateRange || state.loading.groupSize || state.loading.markup
    },
  },
  actions: {
    initialize({
      groupSize,
      selectedDateRange,
      price,
      markupCents,
    }: {
      groupSize: string
      selectedDateRange: { startDate: Date; endDate: Date }
      price: number
      markupCents: number
    }) {
      if (groupSize) {
        this.setGroupSize(groupSize?.replace('people', ''))
      }
      if (selectedDateRange) {
        this.setSelectedDateRange(selectedDateRange)
      }
      if (price) {
        this.setPrice(price)
      }
      if (markupCents) {
        const markup = Number(markupCents) / 100
        this.setMarkup(markup)
        this.setIsMarkupEnabled(true)
      }
    },
    setGroupSize(groupSize: string) {
      this.groupSize = groupSize
    },
    setSelectedDateRange(selectedDateRange: { startDate: Date; endDate: Date }) {
      this.selectedDateRange = selectedDateRange
    },
    setIsMarkupEnabled(isMarkupEnabled: boolean) {
      this.isMarkupEnabled = isMarkupEnabled
    },
    setMarkup(markup: number) {
      this.markup = markup
    },
    setPrice(price: number) {
      this.price = price
    },
    setIsFeedbackOpen(isFeedbackOpen: boolean) {
      this.isFeedbackOpen = isFeedbackOpen
    },
    async fetchAvailabilities(itineraryRequestId: string) {
      const availability = await getItineraryAvailabilities(itineraryRequestId)

      if (!availability.available_date_range) {
        this.availability = []
        return
      }

      this.availability = availability.available_date_range
    },
    async updateCustomItinerary({
      customItineraryId,
      itineraryRequestId,
      startDate,
      groupSize,
      markupInDollars,
      field,
    }: {
      customItineraryId: string
      itineraryRequestId: string
      startDate: DateOnly
      groupSize: string
      markupInDollars: number | null
      field: keyof Loading
    }) {
      this.loading[field] = true
      this.errors[field] = null

      try {
        const customItinerary = await patchCustomItinerary({
          customItineraryId,
          itineraryRequestId,
          startDate,
          groupSize,
          markupInDollars,
        })
        const response: CustomItinerary = await deserializeJsonApi(customItinerary)
        this.setPrice(response.minimumCheckoutTierPricing.priceCents)
        const markup = Number(response.markupCents) / 100
        this.setMarkup(markup)
        this.resetErrors()
      } catch (error) {
        const errors = (error as AxiosError<{ errors: string[] }>).response?.data?.errors
        const status = (error as AxiosError).response?.status

        if (errors || status === 500 || status === 422) {
          this.errors[field] = errors ?? ['Unexpected error']
        }
      } finally {
        this.resetLoading()
      }
    },
    resetErrors() {
      this.errors = {
        dateRange: null,
        groupSize: null,
        markup: null,
      }
    },
    resetLoading() {
      this.loading = {
        dateRange: false,
        groupSize: false,
        markup: false,
      }
    },
  },
})

interface ItineraryAvailabilities {
  available_date_range: [number, number][]
}

interface ItineraryPrice {
  monthly_price: {
    currency: string
    amount_cents: number
  }
}

const getItineraryAvailabilities = async (itineraryRequestId: string) => {
  const { data } = await api.get<ItineraryAvailabilities>('/api/plan_a_trip/recommendation/available_date_range', {
    params: { itinerary_request_id: itineraryRequestId },
  })
  return data
}

const patchCustomItinerary = async ({
  customItineraryId,
  itineraryRequestId,
  startDate,
  groupSize,
  markupInDollars,
}: {
  customItineraryId: string
  itineraryRequestId: string
  startDate: DateOnly
  markupInDollars: number | null
  groupSize: string
}) => {
  const { data } = await api.patch<ItineraryPrice>(`/api/traveler/custom_itineraries/${customItineraryId}`, {
    itinerary_request_id: itineraryRequestId,
    hero_input_start_date: startDate,
    hero_input_group_size: groupSize,
    hero_input_markup_cents: markupInDollars != null ? markupInDollars * 100 : 0,
  })
  return data
}
